import { useMemo, useState } from "react";
import type { AdoptionRow, MonthRow, OfficerRow } from "./types";
import type { BucketShare } from "./stats";

// Palette
export const C = {
  with: "#1a78c2",
  without: "#94a3b8",
  accent: "#f97316",
  good: "#10b981",
  bad: "#ef4444",
  gridline: "rgba(148, 163, 184, 0.25)",
  text: "#475569",
  textDim: "#94a3b8",
};

function formatMonth(ym: string): string {
  const [y, m] = ym.split("-");
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleString("en-US", { month: "short", year: "2-digit" });
}

// =============== Monthly Score Trend =====================================

type MonthlyTrendProps = {
  data: MonthRow[];
};

export function MonthlyTrend({ data }: MonthlyTrendProps) {
  const months = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.month))).sort();
  }, [data]);

  const byMonth = useMemo(() => {
    const map: Record<string, { with?: MonthRow; without?: MonthRow }> = {};
    for (const m of months) map[m] = {};
    for (const row of data) {
      map[row.month][row.has_checklist ? "with" : "without"] = row;
    }
    return map;
  }, [data, months]);

  const width = 880;
  const height = 340;
  const pad = { top: 30, right: 30, bottom: 60, left: 50 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const allMeans = data.map((d) => d.mean).filter((v) => Number.isFinite(v));
  const yMin = Math.floor(Math.min(...allMeans, 60) / 5) * 5;
  const yMax = Math.ceil(Math.max(...allMeans, 85) / 5) * 5;

  const xForMonth = (m: string) => {
    const i = months.indexOf(m);
    if (months.length <= 1) return pad.left + plotW / 2;
    return pad.left + (plotW * i) / (months.length - 1);
  };
  const yFor = (v: number) => pad.top + plotH * (1 - (v - yMin) / (yMax - yMin));

  const path = (series: "with" | "without") => {
    const pts: string[] = [];
    for (const m of months) {
      const row = byMonth[m][series];
      if (!row) continue;
      const x = xForMonth(m);
      const y = yFor(row.mean);
      pts.push(`${pts.length === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`);
    }
    return pts.join(" ");
  };

  const [hover, setHover] = useState<{ month: string; x: number; y: number } | null>(null);

  const yTicks: number[] = [];
  for (let v = yMin; v <= yMax; v += 5) yTicks.push(v);

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="Monthly score comparison">
        {/* Gridlines */}
        {yTicks.map((v) => (
          <g key={v}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={yFor(v)}
              y2={yFor(v)}
              stroke={C.gridline}
              strokeDasharray="3 3"
            />
            <text x={pad.left - 10} y={yFor(v) + 4} fontSize="11" fill={C.textDim} textAnchor="end">
              {v}
            </text>
          </g>
        ))}

        {/* Adoption-collapse shading */}
        {(() => {
          const from = months.indexOf("2026-01");
          const to = months.length - 1;
          if (from < 0 || from > to) return null;
          const x1 = xForMonth(months[from]) - 20;
          const x2 = xForMonth(months[to]) + 20;
          return (
            <g>
              <rect
                x={x1}
                y={pad.top}
                width={x2 - x1}
                height={plotH}
                fill="rgba(148, 163, 184, 0.08)"
              />
              <text
                x={(x1 + x2) / 2}
                y={pad.top + 14}
                textAnchor="middle"
                fontSize="10"
                fill={C.textDim}
                fontStyle="italic"
              >
                adoption &lt; 10%, small sample
              </text>
            </g>
          );
        })()}

        {/* Lines */}
        <path d={path("without")} stroke={C.without} strokeWidth="2.5" fill="none" />
        <path d={path("with")} stroke={C.with} strokeWidth="3" fill="none" />

        {/* Points */}
        {months.flatMap((m) => {
          const dots: any[] = [];
          (["with", "without"] as const).forEach((series) => {
            const row = byMonth[m][series];
            if (!row) return;
            const x = xForMonth(m);
            const y = yFor(row.mean);
            const color = series === "with" ? C.with : C.without;
            dots.push(
              <circle
                key={`${m}-${series}`}
                cx={x}
                cy={y}
                r={series === "with" ? 5 : 4}
                fill="white"
                stroke={color}
                strokeWidth="2.5"
                onMouseEnter={() => setHover({ month: m, x, y })}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: "pointer" }}
              />
            );
          });
          return dots;
        })}

        {/* X axis */}
        {months.map((m) => (
          <g key={m}>
            <text
              x={xForMonth(m)}
              y={height - pad.bottom + 20}
              fontSize="11"
              fill={C.text}
              textAnchor="middle"
            >
              {formatMonth(m)}
            </text>
          </g>
        ))}

        {/* Legend */}
        <g transform={`translate(${pad.left},${height - 18})`}>
          <g>
            <circle cx="6" cy="0" r="5" fill="white" stroke={C.with} strokeWidth="2.5" />
            <text x="18" y="4" fontSize="11" fill={C.text}>
              With checklist (mean score)
            </text>
          </g>
          <g transform="translate(210, 0)">
            <circle cx="6" cy="0" r="4" fill="white" stroke={C.without} strokeWidth="2.5" />
            <text x="18" y="4" fontSize="11" fill={C.text}>
              Without checklist (mean score)
            </text>
          </g>
        </g>

        {/* Hover tooltip */}
        {hover && (() => {
          const w = byMonth[hover.month].with;
          const wo = byMonth[hover.month].without;
          const tooltipW = 190;
          const tooltipH = (w ? 30 : 0) + (wo ? 30 : 0) + 24;
          let tx = hover.x + 10;
          if (tx + tooltipW > width) tx = hover.x - tooltipW - 10;
          const ty = Math.max(pad.top, hover.y - tooltipH - 5);
          return (
            <g style={{ pointerEvents: "none" }}>
              <rect
                x={tx}
                y={ty}
                width={tooltipW}
                height={tooltipH}
                rx="6"
                fill="white"
                stroke="#e2e8f0"
                filter="url(#tip-shadow)"
              />
              <text x={tx + 10} y={ty + 16} fontSize="12" fontWeight="600" fill="#0f172a">
                {formatMonth(hover.month)}
              </text>
              {w && (
                <text x={tx + 10} y={ty + 36} fontSize="11" fill={C.with}>
                  With: {w.mean.toFixed(1)} (n={w.n})
                </text>
              )}
              {wo && (
                <text x={tx + 10} y={ty + 36 + (w ? 18 : 0)} fontSize="11" fill={C.textDim}>
                  Without: {wo.mean.toFixed(1)} (n={wo.n})
                </text>
              )}
            </g>
          );
        })()}

        <defs>
          <filter id="tip-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
          </filter>
        </defs>
      </svg>
    </div>
  );
}

// =============== Adoption Bars =====================================

type AdoptionChartProps = {
  data: AdoptionRow[];
};

export function AdoptionChart({ data }: AdoptionChartProps) {
  const sorted = [...data].sort((a, b) => a.month.localeCompare(b.month));
  const width = 880;
  const height = 280;
  const pad = { top: 30, right: 20, bottom: 60, left: 50 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const barW = plotW / sorted.length - 12;
  const maxTotal = Math.max(...sorted.map((d) => d.total_cases), 1);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="Adoption trajectory">
        {/* Y gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
          const v = Math.round(maxTotal * frac);
          const y = pad.top + plotH * (1 - frac);
          return (
            <g key={frac}>
              <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke={C.gridline} strokeDasharray="3 3" />
              <text x={pad.left - 10} y={y + 4} fontSize="11" fill={C.textDim} textAnchor="end">
                {v}
              </text>
            </g>
          );
        })}

        {sorted.map((row, i) => {
          const x = pad.left + (plotW * i) / sorted.length + 6;
          const totalH = (row.total_cases / maxTotal) * plotH;
          const withH = (row.with_checklist / maxTotal) * plotH;
          const yTotal = pad.top + plotH - totalH;
          const yWith = pad.top + plotH - withH;
          const rate = row.total_cases > 0 ? (row.with_checklist / row.total_cases) * 100 : 0;
          const isHover = hoverIdx === i;
          return (
            <g key={row.month} onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)}>
              <rect
                x={x}
                y={yTotal}
                width={barW}
                height={totalH}
                fill={C.without}
                opacity={isHover ? 0.9 : 0.5}
                rx="3"
              />
              <rect
                x={x}
                y={yWith}
                width={barW}
                height={withH}
                fill={C.with}
                opacity={isHover ? 1 : 0.9}
                rx="3"
              />
              <text
                x={x + barW / 2}
                y={Math.max(yWith - 6, pad.top - 4)}
                fontSize="11"
                fontWeight="600"
                fill={rate >= 10 ? C.with : C.textDim}
                textAnchor="middle"
              >
                {rate.toFixed(0)}%
              </text>
              <text
                x={x + barW / 2}
                y={height - pad.bottom + 20}
                fontSize="11"
                fill={C.text}
                textAnchor="middle"
              >
                {formatMonth(row.month)}
              </text>
              {isHover && (
                <g style={{ pointerEvents: "none" }}>
                  <rect
                    x={x + barW / 2 - 70}
                    y={yTotal - 56}
                    width={140}
                    height={48}
                    rx="6"
                    fill="white"
                    stroke="#e2e8f0"
                    filter="url(#tip-shadow)"
                  />
                  <text x={x + barW / 2} y={yTotal - 38} fontSize="11" fontWeight="600" fill="#0f172a" textAnchor="middle">
                    {formatMonth(row.month)}
                  </text>
                  <text x={x + barW / 2} y={yTotal - 22} fontSize="11" fill={C.with} textAnchor="middle">
                    {row.with_checklist}/{row.total_cases} ({rate.toFixed(0)}%)
                  </text>
                </g>
              )}
            </g>
          );
        })}

        <g transform={`translate(${pad.left},${height - 18})`}>
          <rect width="12" height="12" y="-9" fill={C.with} rx="2" />
          <text x="18" y="1" fontSize="11" fill={C.text}>
            With checklist
          </text>
          <rect x="130" width="12" height="12" y="-9" fill={C.without} opacity="0.5" rx="2" />
          <text x="148" y="1" fontSize="11" fill={C.text}>
            Total Manchester cases
          </text>
        </g>
        <defs>
          <filter id="tip-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
          </filter>
        </defs>
      </svg>
    </div>
  );
}

// =============== Score Distribution ========================

export function ScoreDistribution({ buckets }: { buckets: BucketShare[] }) {
  const width = 520;
  const height = 240;
  const pad = { top: 20, right: 20, bottom: 60, left: 40 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const groupW = plotW / buckets.length;
  const barW = groupW * 0.38;
  const maxPct = Math.max(70, Math.ceil(Math.max(...buckets.flatMap((b) => [b.with, b.without])) / 10) * 10);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="Score distribution">
      {[0, 20, 40, 60].filter((v) => v < maxPct).map((v) => {
        const y = pad.top + plotH * (1 - v / maxPct);
        return (
          <g key={v}>
            <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke={C.gridline} strokeDasharray="3 3" />
            <text x={pad.left - 8} y={y + 4} fontSize="10" fill={C.textDim} textAnchor="end">
              {v}%
            </text>
          </g>
        );
      })}
      {buckets.map((b, i) => {
        const xCenter = pad.left + groupW * (i + 0.5);
        const wH = (b.with / maxPct) * plotH;
        const woH = (b.without / maxPct) * plotH;
        const yW = pad.top + plotH - wH;
        const yWo = pad.top + plotH - woH;
        return (
          <g key={b.label}>
            <rect x={xCenter - barW - 2} y={yW} width={barW} height={wH} fill={C.with} rx="3">
              <title>{`With checklist: ${b.with}%`}</title>
            </rect>
            <rect x={xCenter + 2} y={yWo} width={barW} height={woH} fill={C.without} opacity="0.8" rx="3">
              <title>{`Without checklist: ${b.without}%`}</title>
            </rect>
            <text x={xCenter - barW / 2 - 2} y={yW - 4} fontSize="10" fill={C.with} fontWeight="600" textAnchor="middle">
              {b.with}%
            </text>
            <text x={xCenter + barW / 2 + 2} y={yWo - 4} fontSize="10" fill={C.textDim} textAnchor="middle">
              {b.without}%
            </text>
            <text x={xCenter} y={height - pad.bottom + 20} fontSize="11" fill={C.text} textAnchor="middle" fontWeight="500">
              {b.label}
            </text>
          </g>
        );
      })}
      <text x={width / 2} y={height - pad.bottom + 44} fontSize="11" fill={C.textDim} textAnchor="middle">
        Score bucket
      </text>
    </svg>
  );
}

// =============== Officer Paired Deltas =====================================

export function OfficerDeltas({ data }: { data: OfficerRow[] }) {
  // Filter to officers with at least 2 cases in each group
  const shown = useMemo(
    () =>
      data
        .filter((d) => d.with_n >= 2 && d.without_n >= 2)
        .sort((a, b) => b.delta - a.delta),
    [data]
  );
  if (shown.length === 0) {
    return <div className="text-sm text-slate-500">No officers with n≥2 in both groups.</div>;
  }

  const rowH = 28;
  const width = 720;
  const height = shown.length * rowH + 40;
  const labelW = 140;
  const nW = 120;
  const barMax = Math.max(...shown.map((d) => Math.abs(d.delta)), 5);
  const plotL = labelW + nW;
  const plotW = width - plotL - 20;
  const centerX = plotL + plotW / 2;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="Per-officer deltas">
      <line x1={centerX} x2={centerX} y1={20} y2={height - 10} stroke="#cbd5e1" strokeDasharray="3 3" />
      <text x={centerX} y={14} fontSize="10" fill={C.textDim} textAnchor="middle">
        no difference
      </text>
      {shown.map((d, i) => {
        const y = 20 + i * rowH;
        const delta = d.delta;
        const w = (Math.abs(delta) / barMax) * (plotW / 2);
        const x = delta >= 0 ? centerX : centerX - w;
        const color = delta >= 0 ? C.good : C.bad;
        return (
          <g key={d.officer_name}>
            <rect x={0} y={y - rowH / 2 + 2} width={width} height={rowH - 4} fill={i % 2 === 0 ? "#f8fafc" : "white"} />
            <text x={labelW - 10} y={y + 4} fontSize="12" fill="#0f172a" textAnchor="end" fontWeight="500">
              {d.officer_name}
            </text>
            <text x={labelW + 4} y={y + 4} fontSize="11" fill={C.textDim}>
              w:{d.with_n} ({d.with_mean.toFixed(0)}) · wo:{d.without_n} ({d.without_mean.toFixed(0)})
            </text>
            <rect x={x} y={y - 6} width={w} height={12} fill={color} rx="2" opacity="0.85" />
            <text
              x={delta >= 0 ? x + w + 6 : x - 6}
              y={y + 4}
              fontSize="11"
              fontWeight="600"
              fill={color}
              textAnchor={delta >= 0 ? "start" : "end"}
            >
              {delta >= 0 ? "+" : ""}
              {delta.toFixed(1)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// =============== Bullet comparison ========================================

export function EngagementBars({
  withRate,
  withoutRate,
  withTotal,
  withoutTotal,
  withImplemented,
  withoutImplemented,
}: {
  withRate: number;
  withoutRate: number;
  withTotal: number;
  withoutTotal: number;
  withImplemented: number;
  withoutImplemented: number;
}) {
  const maxRate = Math.max(withRate, withoutRate, 1) * 1.2;
  const bar = (rate: number, color: string, label: string, sub: string, bold?: boolean) => (
    <div className="mb-5">
      <div className="flex justify-between text-sm mb-1">
        <span className={`${bold ? "font-semibold" : ""} text-slate-700`}>{label}</span>
        <span className={`${bold ? "font-semibold" : ""}`} style={{ color }}>
          {rate.toFixed(2)}%
        </span>
      </div>
      <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${(rate / maxRate) * 100}%`, backgroundColor: color }}
        />
      </div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );

  return (
    <div>
      {bar(
        withRate,
        C.with,
        "With checklist",
        `${withImplemented} implemented / ${withTotal} suggestions`,
        true
      )}
      {bar(
        withoutRate,
        C.without,
        "Without checklist",
        `${withoutImplemented} implemented / ${withoutTotal} suggestions`
      )}
      <div className="mt-3 text-sm bg-blue-50 text-blue-900 rounded-md p-3">
        <span className="font-semibold">{(withRate / withoutRate).toFixed(2)}×</span> higher
        implementation rate when officers use a checklist.
      </div>
    </div>
  );
}

// =============== Gauge / score ring ========================================

export function ScoreRing({ score, size = 80, label }: { score: number | null; size?: number; label?: string }) {
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score));
  const stroke = 8;
  const r = size / 2 - stroke;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const color =
    pct >= 85 ? "#10b981" : pct >= 75 ? "#1a78c2" : pct >= 65 ? "#f59e0b" : "#ef4444";
  return (
    <div className="inline-flex flex-col items-center" style={{ width: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x={size / 2}
          y={size / 2 + 5}
          textAnchor="middle"
          fontSize={size / 3.2}
          fontWeight="700"
          fill="#0f172a"
        >
          {score == null ? "n/a" : Math.round(score)}
        </text>
      </svg>
      {label && <div className="text-xs text-slate-500 mt-1">{label}</div>}
    </div>
  );
}
